import { trackEvent } from '../../utils/analytics';

/**
 * GA4 ecommerce events for the paid Advanced Assessment.
 *
 * The checkout hands the visitor to Stripe and returns them to a freshly loaded
 * page, so the price shown when checkout began is parked in localStorage and read
 * back for `purchase`. The commerce status API reports only the current offer,
 * never the amount a given order paid, so the current offer is never used as a
 * purchase value: with no recorded price the purchase is sent without a value
 * rather than with a wrong one (a launch-price order must not be re-reported at
 * the standard price).
 *
 * Privacy: only the session id (the GA4 `transaction_id`), the price and the
 * currency leave the page. Never add an email, name or organisation.
 */
const CURRENCY = 'CAD';
const priceKey = (sessionId: string) => `davinci-assessment-price-${sessionId}`;
const purchaseSentKey = (sessionId: string) => `davinci-assessment-purchase-sent-${sessionId}`;

const readStorage = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private mode or blocked storage: the in-page record below still guards this load.
  }
};

const recallPrice = (sessionId: string): number | null => {
  const stored = Number(readStorage(priceKey(sessionId)));
  return Number.isFinite(stored) && stored > 0 ? stored : null;
};

/** Call immediately before the redirect to Stripe. Uses a beacon so the redirect cannot cancel the hit. */
export function trackAdvancedBeginCheckout(sessionId: string, valueCad: number): void {
  writeStorage(priceKey(sessionId), String(valueCad));
  trackEvent('begin_checkout', {
    value: valueCad,
    currency: CURRENCY,
    transport_type: 'beacon',
  });
}

/** True only on the Stripe return (`?checkout=success`), never on a later revisit or resume link. */
const isCheckoutSuccessReturn = (): boolean => {
  try {
    return new URLSearchParams(window.location.search).get('checkout') === 'success';
  } catch {
    return false;
  }
};

/**
 * Report a confirmed payment, once per paid session.
 *
 * Fires only when the URL carries the checkout-success marker and the server says
 * `paid`, and records a sent flag per session id in localStorage (plus a window
 * record, because webpack duplicates this module into each chunk and storage can
 * be blocked). A bookmark, a recovery link or the "Resume" reload therefore never
 * produces a second purchase.
 */
export function trackAdvancedPurchase(sessionId: string): void {
  if (!isCheckoutSuccessReturn()) return;

  const w = window as unknown as { __purchasesFired?: Record<string, true> };
  const fired = (w.__purchasesFired = w.__purchasesFired || {});
  if (fired[sessionId] || readStorage(purchaseSentKey(sessionId))) return;
  fired[sessionId] = true;
  writeStorage(purchaseSentKey(sessionId), '1');

  const value = recallPrice(sessionId);
  trackEvent('purchase', {
    transaction_id: sessionId,
    ...(value !== null ? { value, currency: CURRENCY } : {}),
  });
}
